import type { FileDto } from '@/api/api.dto';

export type ChatRoomUserRole = 'creator' | 'member' | 'admin' | 'user';

export type ChatRoomUser = {
  id: string;
  name: string;
  surname: string;
  display_name: string;
  avatar: FileDto | null;
  role: ChatRoomUserRole;
  allowed_to_write?: boolean;
};

export type LastMessage = {
  id: string;
  text: string;
  type?: string;
  status: string;
  created_at: string;
  updated_at?: string;
  creator_id: string;
  room_id?: string;
  read?: boolean;
  thread_id?: string | null;
  thread_messages_count?: number;
  quote_message_id?: string | null;
  media_files?: {
    id: string;
    file_name?: string;
    preview_file_name?: string;
    name?: string;
    type?: string;
    url?: string;
    size?: number;
  }[];
  message_history?: unknown;
  quote_message?: unknown;
  mentions?: Record<string, string>;
  mentions_count?: number;
};

export type ChatRoomType = 'one_to_one' | 'broadcast' | 'temporary' | 'group' | string;

export const isMemberAddable = (type: ChatRoomType | undefined): boolean =>
  type !== 'one_to_one' && type !== 'broadcast' && type !== 'temporary';

export const canPinInRoom = (
  type: ChatRoomType | undefined,
  userRole: ChatRoomUserRole | undefined,
): boolean => type !== 'temporary' || userRole === 'creator';

export type ChatRoom = {
  id: string;
  name: string;
  description: string;
  image: FileDto | null;
  creator_id: string;
  type?: string;
  folder_ids: string[];
  topic_code?: string;
  /** The account the room was opened from: for `email` and `bid` rooms the client vessel, for a
   * chat whichever vessel or vendor its creator was acting as. */
  service_id?: string;
  last_message: LastMessage | null;
  room_unread_messages: number;
  users: ChatRoomUser[];
  mute: boolean;
  pinned?: boolean;
  created_at: string;
  updated_at: string;
  remove_after?: string | null;
  gallery?: string | null;
};

/**
 * A room a vessel opened with a vendor — its general chat or a bid. Vendor side, the vessel is a
 * client.
 */
export const isVesselClientRoom = (room: ChatRoom) => room.type === 'email' || room.type === 'bid';

/**
 * A room of the active account, vessel or vendor — a chat opened from it, or one the account is a
 * member of, as both sides of a client's room are. `/rooms` also returns every room the person is
 * in themselves, whichever account is active, so that alone does not place a room. A chat
 * somebody else opened from their account is filed under none of the person's, so it lists in
 * All chats, Unread and Mentions alone (see spansAccounts).
 */
export const inAccountScope = (room: ChatRoom, accountId: string | null) =>
  room.service_id === accountId || (room.users ?? []).some((user) => user.id === accountId);

export type BidRoom = Omit<ChatRoom, 'users'> & {
  users: ChatRoomUser[] | null;
  topic_code: string;
  pinned: boolean;
};

export type CreateChatRoom = {
  name: string;
  description: string;
  type?: string;
  folder_id?: string;
};

export type AssignRolePayload = {
  usersIds: string[];
  role: string;
};

export type MarkMessagesAsReadPayload = {
  message_ids: string[];
};

export type UpdateMessageReq = {
  id: string;
  quote_message_id?: string;
  text?: string;
};

export type SearchMessage = {
  attachments_count: number;
  id: string;
  room_id: string;
  room_name: string;
  creator_id: string;
  text: string;
};

export type SearchRoomHit = {
  description: string;
  id: string;
  name: string;
};

export type SearchResults = {
  messages: SearchMessage[];
  rooms: SearchRoomHit[];
};
